import React, { useEffect, useMemo, useState } from 'react';
import _ from 'lodash';

import styles from './QuizResultOverall.module.scss';
import Badge from '../../UI/Badge/Badge';
import Icon from '../../UI/Icon/Icon';
import Tooltip from '../../UI/Tooltip/Tooltip';
import Help from '../../UI/Help/Help';
import { useContextSelector } from 'use-context-selector';
import { UIContext } from '../../context/UI/UIContext';
import Alert from '../../UI/Alert/Alert';
import Label from '../../context/Label/Label';
import useLabel from '../../context/Label/useLabel';
import { getOverallScore } from '../../selectors/overalScoreSelector';

function QuizResultOverall({
  resourceId,
  globalState,
  showResults,
}: {
  resourceId: string;
  globalState: any;
  showResults: boolean;
}) {
  const { course, tracking, setCourseScore, onPageCompleted } = globalState;
  const startBlinkNext = useContextSelector(UIContext, (c) => c.startBlinkNext);
  const { plainLabel } = useLabel();
  const [expanded, setExpanded] = useState<boolean>(false);

  const overallScore = useMemo(
    () => getOverallScore(course, tracking),
    [course, tracking]
  );

  const isThisQuizInOverallScore = !!_.find(
    overallScore.scoreByQuiz,
    (s) => !!resourceId && s.resourceId === resourceId
  );

  useEffect(() => {
    // mark page as completed
    onPageCompleted(() => startBlinkNext());
  }, []); // eslint-disable-line

  useEffect(() => {
    // valid only is all quizzes (could be only one) are/is completed
    if (!overallScore.completed) return;

    // set course score
    setCourseScore(+overallScore.score);
  }, [overallScore.completed]); // eslint-disable-line

  if (!isThisQuizInOverallScore) {
    return (
      <div className={styles.root} data-testid="QuizResultOverall">
        <Alert className={styles.alert}>
          <Label>
            This score will not be computed in your final overall score
          </Label>
        </Alert>
      </div>
    );
  }

  // if only one quiz is available to be graded, does not show this component on screen
  if (_.size(overallScore.scoreByQuiz) <= 1) return null;

  if (!showResults) return null;

  return (
    <div className={styles.root} data-testid="QuizResultOverall">
      <span className={styles.title}>
        <Label>Overall score</Label>{' '}
        <Help
          tooltip={plainLabel(
            'It will be reported as your final score when you complete all graded quiz on this course.'
          )}
        />
      </span>
      <div className={styles.detail}>
        <Tooltip
          tooltip={
            overallScore.completed
              ? plainLabel(
                  'Based on all graded quizzes answered on this course.'
                )
              : plainLabel(
                  'Complete all graded quizzes on this course for the final overall score.'
                )
          }
          id={`overall`}
        >
          <Badge
            success={
              overallScore.completed &&
              overallScore.score >= overallScore.passingScore
            }
            danger={
              overallScore.completed &&
              overallScore.score < overallScore.passingScore
            }
            cancel={!overallScore.completed}
            className={[styles.badge, styles.badgeMain].join(' ')}
            onClick={() => setExpanded(!expanded)}
          >
            {overallScore.score}%
          </Badge>
        </Tooltip>
        <Icon
          icon={expanded ? 'chevron-left' : 'chevron-right'}
          onClick={() => setExpanded(!expanded)}
          className={styles.iconToggleExpand}
        />
        <div className={!expanded ? styles.hidden : ''}>
          {_.map(
            overallScore.scoreByQuiz,
            ({ title, score, passingScore, completed }, i) => (
              <Tooltip
                tooltip={`${title}</br>(${plainLabel(
                  'Passing score'
                )}: ${passingScore}%)`}
                bottom
                key={i}
                id={`quiz-${i}`}
              >
                <Badge
                  success={completed && score >= passingScore}
                  danger={completed && score < passingScore}
                  cancel={!completed}
                  className={styles.badge}
                >
                  {completed ? `${score}%` : '-'}
                </Badge>
              </Tooltip>
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default QuizResultOverall;
